import React from 'react';
import {
  Box, Typography, Chip,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { JournalEntry } from '../types';
import { getCategoryColor, rainbowColor } from '../utils';
import { ColumnDef, ColumnId } from '../hooks/useColumnVisibility';

interface JournalTableRowProps {
  row: JournalEntry;
  globalIdx: number;
  pharmacies: string[];
  visibleDefs: ColumnDef[];
  gridTemplate: string;
}

/**
 * Одна строка таблицы журнала.
 * Рендерит только видимые колонки (из useColumnVisibility).
 * Первые три ключевые колонки закреплены по горизонтали (sticky).
 */
const JournalTableRow: React.FC<JournalTableRowProps> = ({
  row, globalIdx, pharmacies, visibleDefs, gridTemplate,
}) => {
  const theme = useTheme();
  const isSeriesMismatch = row.comment === 'Серия не совпадает';
  const catColors = getCategoryColor(row.categoryName);
  const pharmacyColorIndex = pharmacies.indexOf(row.pharmacyNum);

  const renderCell = (col: ColumnDef) => {
    switch (col.id as ColumnId) {
      case 'idx':
        return (
          <Typography variant="body2" color="text.secondary" fontWeight={500} sx={{ fontSize: '0.8rem' }}>
            {globalIdx}
          </Typography>
        );

      case 'pharmacy':
        return (
          <Chip
            label={`№${row.pharmacyNum}`}
            size="small"
            variant="outlined"
            sx={{
              fontWeight: 700, fontSize: '0.7rem', borderRadius: '6px', height: 22,
              borderColor: rainbowColor(pharmacyColorIndex, pharmacies.length),
              color: rainbowColor(pharmacyColorIndex, pharmacies.length),
            }}
          />
        );

      case 'region':
        return (
          <Typography variant="body2" color="text.secondary" noWrap title={row.cityId || '—'} sx={{ fontSize: '0.8rem' }}>
            {row.cityId || '—'}
          </Typography>
        );

      case 'name':
        return (
          <Typography variant="body2" fontWeight={500} noWrap title={row.materialName} sx={{ fontSize: '0.8rem' }}>
            {row.materialName}
          </Typography>
        );

      case 'producer':
        return (
          <Typography variant="caption" color="text.secondary" noWrap title={row.producer} sx={{ fontSize: '0.72rem' }}>
            {row.producer || <span style={{ fontStyle: 'italic', color: '#94a3b8' }}>—</span>}
          </Typography>
        );

      case 'expiry':
        return (
          <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.8rem' }}>
            {row.expiryDate ? row.expiryDate.split('T')[0] : '—'}
          </Typography>
        );

      case 'serial':
        return row.serialNumber
          ? (
            <Chip
              label={row.serialNumber}
              size="small"
              variant="outlined"
              sx={{
                fontFamily: 'monospace', fontSize: '0.7rem', height: 22,
                borderColor: isSeriesMismatch ? 'text.secondary' : 'divider',
                color: isSeriesMismatch ? 'text.secondary' : 'text.primary',
                bgcolor: isSeriesMismatch ? 'action.hover' : 'transparent',
              }}
            />
          )
          : <Typography variant="caption" color="text.disabled" fontStyle="italic" sx={{ fontSize: '0.72rem' }}>нет</Typography>;

      case 'qty':
        return (
          <Typography variant="body2" fontWeight={600} align="right" sx={{ fontSize: '0.8rem' }}>
            {row.quantity.toLocaleString('ru-RU')} уп.
          </Typography>
        );

      case 'qtyPcs':
        return (
          <Typography variant="body2" color="text.secondary" align="right" sx={{ fontSize: '0.75rem' }}>
            {row.quantityPcs !== undefined && row.quantityPcs > 0
              ? `${row.quantityPcs.toLocaleString('ru-RU')} шт.`
              : '—'}
          </Typography>
        );

      case 'price':
        return (
          <Typography variant="body2" color="text.secondary" align="right" sx={{ fontSize: '0.75rem' }}>
            {row.price.toLocaleString('ru-RU')} ₸
          </Typography>
        );

      case 'totalSum':
        return (
          <Typography variant="body2" fontWeight={700} color="success.main" align="right" sx={{ fontSize: '0.8rem' }}>
            {row.totalSum.toLocaleString('ru-RU')} ₸
          </Typography>
        );

      case 'manager':
        return (
          <Typography variant="caption" color="text.secondary" noWrap title={row.managerName} sx={{ fontSize: '0.72rem' }}>
            {row.managerName}
          </Typography>
        );

      case 'category':
        return (
          <Chip
            label={row.categoryName}
            size="small"
            sx={{
              bgcolor: catColors.bg, color: catColors.text,
              fontWeight: 600, fontSize: '0.7rem', borderRadius: '6px', height: 22,
            }}
          />
        );

      case 'priceGroup':
        return (
          <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.72rem' }}>
            {row.priceGroup || '—'}
          </Typography>
        );

      case 'code':
        return (
          <Typography variant="body2" fontWeight={600} fontFamily="monospace" sx={{ fontSize: '0.75rem' }}>
            {row.nomenclatureCode}
          </Typography>
        );

      case 'barcode':
        return (
          <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.72rem' }}>
            {row.barcode || '—'}
          </Typography>
        );

      case 'rxotc':
        return (
          <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.72rem' }}>
            {row.rxotc || '—'}
          </Typography>
        );

      case 'comment':
        return (
          <Typography variant="caption" color="text.secondary" noWrap title={row.comment} sx={{ fontSize: '0.72rem' }}>
            {row.comment || '—'}
          </Typography>
        );

      case 'recordDate':
        return (
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>
            {row.recordDate ? new Date(row.recordDate).toLocaleDateString('ru-RU') : '—'}
          </Typography>
        );

      case 'createdBy':
        return (
          <Typography variant="caption" color="text.secondary" noWrap title={row.createdBy} sx={{ fontSize: '0.72rem' }}>
            {row.createdBy || '—'}
          </Typography>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{
      display: 'grid',
      gridTemplateColumns: gridTemplate,
      gap: 1, px: 2, alignItems: 'center',
      borderBottom: '1px solid', borderColor: 'divider',
      minWidth: 'max-content', minHeight: 32,
      '&:hover': {
        bgcolor: alpha(theme.palette.primary.main, 0.03),
      },
    }}>
      {visibleDefs.map(col => {
        const isRightAligned = ['qty', 'qtyPcs', 'price', 'totalSum'].includes(col.id);
        return (
          <Box
            key={col.id}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: isRightAligned ? 'flex-end' : 'flex-start',
              width: '100%',
            }}
          >
            {renderCell(col)}
          </Box>
        );
      })}
    </Box>
  );
};

export default JournalTableRow;
